import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export interface IdeaGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (authorIdea?: string, referencePostUrl?: string, reelsType?: string) => void;
  isGenerating: boolean;
}



// Компонент модального окна генерации по идее
export const IdeaGenerationModal: React.FC<IdeaGenerationModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating
}) => {
  const [authorIdea, setAuthorIdea] = useState('');
  const [referencePostUrl, setReferencePostUrl] = useState('');
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [reelsType, setReelsType] = useState<string | undefined>('Экспертный');
  
  // Создаём корневой элемент для портала
  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    setModalRoot(root);
    
    return () => {
      if (root && !root.hasChildNodes() && root.parentElement) {
        root.parentElement.removeChild(root);
      }
    };
  }, []);

  // Блокировка прокрутки при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Сброс полей при закрытии
  useEffect(() => {
    if (!isOpen) {
      setAuthorIdea('');
      setReferencePostUrl('');
      setIsValidUrl(true);
      setReelsType('Экспертный');
    }
  }, [isOpen]);

  // Валидация ссылки на Instagram пост
  const validateInstagramUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Пустая ссылка допустима
    
    try {
      new URL(url);
      const lowerUrl = url.toLowerCase();
      const instagramRegex = /^https?:\/\/(www\.)?instagram\.com\/(p|reel)\/[a-zA-Z0-9_-]+\/?.*$/;
      return instagramRegex.test(lowerUrl);
    } catch {
      return false;
    }
  };

  // Обработчик изменения ссылки
  const handleReferenceUrlChange = (value: string) => {
    setReferencePostUrl(value);
    setIsValidUrl(validateInstagramUrl(value));
  };

  // Функция для автоматического изменения высоты textarea
  const autoResizeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleGenerate = () => {
    const hasAuthorIdea = authorIdea.trim().length > 0;
    const hasValidUrl = referencePostUrl.trim().length > 0 && isValidUrl;
    
    if (hasAuthorIdea || hasValidUrl) {
      onGenerate(
        hasAuthorIdea ? authorIdea.trim() : undefined,
        hasValidUrl ? referencePostUrl.trim() : undefined,
        reelsType
      );
    }
  };

  const hasAuthorIdea = authorIdea.trim().length > 0;
  const hasValidUrl = referencePostUrl.trim().length > 0 && isValidUrl;
  const hasReelsType = !!reelsType;
  const canGenerate = (hasAuthorIdea || hasValidUrl) && hasReelsType && !isGenerating;

  if (!isOpen || !modalRoot) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-none sm:rounded-xl shadow-2xl w-full h-[100dvh] sm:h-auto sm:max-w-2xl sm:mx-auto sm:max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
            Рилс по идее
          </h3>
          <button 
            onClick={onClose}
            disabled={isGenerating}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Контент */}
        <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 pb-24 sm:pb-8">
          {/* Подсказка по заполнению */}
          <div className="text-sm text-yellow-600 dark:text-yellow-400 p-0">
            Необходимо заполнить хотя бы <span className="font-medium">одно</span> поле.
          </div>
          {/* Поле для идеи автора */}
          <div>
            <label className="block text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
              Идея автора
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Опишите тему и ключевую мысль ролика в 1–2 предложениях.
            </p>
            <textarea
              value={authorIdea}
              onChange={(e) => {
                setAuthorIdea(e.target.value);
                autoResizeTextarea(e);
              }}
              placeholder="Например: ролик про выбор ниши и первые шаги"
              className="w-full h-28 sm:h-32 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none text-sm sm:text-base leading-relaxed"
              disabled={isGenerating}
              style={{ minHeight: '128px' }}
            />
          </div>

          {/* Поле для ссылки на референсный пост */}
          <div>
            <label className="block text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
              Референс (Instagram)
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Вставьте ссылку на пост/рилс, механику которого хотите адаптировать.
            </p>
            <input
              type="url"
              value={referencePostUrl}
              onChange={(e) => handleReferenceUrlChange(e.target.value)}
              placeholder="https://instagram.com/p/..."
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm sm:text-base ${
                isValidUrl 
                  ? 'border-gray-300 dark:border-gray-600' 
                  : 'border-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-600 dark:focus:ring-red-600 dark:focus:border-red-600'
              } dark:placeholder-gray-400`}
              disabled={isGenerating}
            />
            {!isValidUrl && (
              <div className="mt-2 text-sm text-red-500 dark:text-red-400">
                Формат: instagram.com/p/... или instagram.com/reel/...
              </div>
            )}
          </div>

          {/* Выбор типа рилса */}
          <div>
            <label className="block text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
              Тип рилса
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {['Продающий', 'Экспертный', 'Развлекательный'].map((type) => {
                const isActive = reelsType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setReelsType(isActive ? undefined : type)}
                    disabled={isGenerating}
                    className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-white'
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 text-gray-700 dark:text-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Кнопки */}
        <div className="fixed sm:sticky bottom-0 left-0 right-0 sm:left-auto sm:right-auto flex justify-end space-x-4 px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur">
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Отмена
          </button>
          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className={`px-8 py-3 rounded-lg transition-colors font-medium flex items-center ${
              canGenerate
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Генерация...
              </>
            ) : (
              <>
                Сгенерировать
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, modalRoot);
};;


